import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { fetchUsageMix, type UsageMixRow } from '@/lib/adminApi';
import {
  DAY_PRESETS,
  parseReportState,
  serializeReportState,
  switchTab,
  type ReportState,
  type ReportTab,
} from '@/lib/reportsState';
import { canReadUsageReports, canReadUserReports } from '@/lib/roles';
import { useMe } from '@/lib/useMe';

// The spend tabs carry recharts; load them only when one is opened.
const SpendReports = lazy(() => import('./reports/SpendReports'));

const TABS: ReadonlyArray<{ id: ReportTab; label: string }> = [
  { id: 'usage', label: 'Usage mix' },
  { id: 'models', label: 'LLM models' },
  { id: 'users', label: 'LLM by user' },
  { id: 'spenders', label: 'Top spenders' },
  { id: 'generations', label: 'Generations' },
];

/** 0.519 -> "51.9%". */
function percent(share: number): string {
  return `${(share * 100).toFixed(1)}%`;
}

/**
 * Reports (ClickUp 869f4c9vb, the owner's decision 19.09.2026: "build the
 * simple thing", no agent). SUPERADMIN ONLY — the nav hides it and the API
 * refuses every other role; a person who types the URL sees the refusal.
 * Two cuts of the window's runs as bar lists with counts and shares. What
 * each cut can and cannot see is written where it is computed
 * (apps/api-py/app/http/usage_mix.py) and repeated under each list here, so
 * a number is never read as more than it is.
 */
export function ReportsPage() {
  const { data: me } = useMe();
  // Tab and filters live in the URL so a link reproduces the view.
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseReportState(searchParams), [searchParams]);
  const update = (next: ReportState) => setSearchParams(serializeReportState(next), { replace: true });
  const patch = (changes: Partial<ReportState>) => update({ ...state, ...changes });
  const spendAllowed = canReadUsageReports(me?.role, me?.capabilities);
  const allowed = canReadUserReports(me?.role) || spendAllowed;
  const tab: ReportTab = state.tab !== 'usage' && !spendAllowed ? 'usage' : state.tab;
  const days = state.days ?? 30;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-reports', 'usage-mix', days],
    queryFn: () => fetchUsageMix(days),
    enabled: allowed && tab === 'usage',
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60_000,
  });

  if (!allowed) {
    return (
      <div>
        <h1 className="text-card-title font-semibold text-fg-primary">Reports</h1>
        <p className="mt-2 text-body text-fg-secondary">
          Reports about users are for the superadmin role only.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-card-title font-semibold text-fg-primary">Reports</h1>
      <p className="mt-1 text-body text-fg-secondary">
        {tab === 'usage'
          ? 'What people run — counts over a window, nobody named.'
          : 'What it costs — credits charged to users and USD paid to providers, named per user where the tab says so.'}
      </p>

      <div className="mt-4 flex flex-wrap gap-2" role="tablist" aria-label="Report">
        {TABS.filter((t) => t.id === 'usage' || spendAllowed).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => update(switchTab(state, t.id))}
            className={`rounded-card border px-3 py-1 text-meta ${
              tab === t.id
                ? 'border-primary bg-surface-brand-subtle text-fg-primary'
                : 'border-border text-fg-secondary hover:bg-state-hover'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab !== 'usage' && (
        <Suspense fallback={<p className="mt-4 text-body text-fg-hint">Loading…</p>}>
          <SpendReports state={state} patch={patch} />
        </Suspense>
      )}

      {tab === 'usage' && (
        <>
      <div className="mt-4 flex items-center gap-2" role="group" aria-label="Window">
        {DAY_PRESETS.map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => patch({ days: w, from: '', to: '' })}
            aria-pressed={days === w}
            className={`rounded-card border px-3 py-1 text-meta ${
              days === w
                ? 'border-primary bg-surface-brand-subtle text-fg-primary'
                : 'border-border text-fg-secondary hover:bg-state-hover'
            }`}
          >
            Last {w} days
          </button>
        ))}
      </div>

      {isLoading && <p className="mt-4 text-body text-fg-hint">Loading…</p>}
      {isError && (
        <p className="mt-4 text-body text-destructive">Could not load the report. Try again.</p>
      )}

      {data && (
        <>
          <p className="mt-4 text-meta text-fg-secondary" data-testid="usage-mix-window">
            <span className="font-semibold text-fg-primary">{data.runs}</span> workflow runs by{' '}
            <span className="font-semibold text-fg-primary">{data.users}</span> users since{' '}
            {new Date(data.since).toLocaleDateString()}.
          </p>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <BarList
              title="By origin"
              rows={data.by_origin}
              testId="usage-mix-origin"
              note="The builtin template a workflow descends from (its base_pipeline_id lineage), else custom. Lineage is recorded only for copies made since the column existed — this is a floor for template use, not a measure of it."
            />
            <BarList
              title="By what it makes"
              rows={data.by_output}
              testId="usage-mix-output"
              note="From the node types in each run's own graph: video, then captions, then image, audio, text — the first that applies. A workflow that renders a video from images counts as video."
            />
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
}

function BarList({
  title,
  rows,
  note,
  testId,
}: {
  title: string;
  rows: UsageMixRow[];
  note: string;
  testId: string;
}) {
  const max = rows.reduce((m, r) => Math.max(m, r.share), 0) || 1;
  return (
    <section className="rounded-card border border-border p-4" data-testid={testId}>
      <h2 className="text-h3 font-semibold text-fg-primary">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-2 text-body text-fg-hint">No runs in this window.</p>
      ) : (
        <ol className="mt-3 flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.key} className="text-body">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate text-fg-primary" title={row.key}>
                  {row.key}
                </span>
                <span className="shrink-0 text-meta text-fg-secondary">
                  {row.runs} · {percent(row.share)}
                  {row.users !== undefined ? ` · ${row.users} users` : ''}
                </span>
              </div>
              <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-surface-muted">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${Math.max(2, (row.share / max) * 100)}%` }}
                  aria-hidden="true"
                />
              </div>
            </li>
          ))}
        </ol>
      )}
      <p className="mt-3 text-meta text-fg-hint">{note}</p>
    </section>
  );
}
